<template>
  <div class="bg-white rounded-2xl shadow p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-xl font-bold text-gray-800">List of MOA's</h1>
      <p class="text-sm text-gray-500 mt-1">Manage and maintain the list of client signed MOA's.</p>
    </div>

    <!-- Controls -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-gray-700">List of MOA's</span>
        <span class="text-xs text-gray-400 bg-gray-100 rounded-full px-2 py-0.5"> {{ totalItems }} listing{{ totalItems !== 1 ? "s" : "" }} </span>
      </div>
      <div class="flex items-center gap-3">
        <!-- Search -->
        <div class="relative">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search company, filename..."
            @input="debouncedFetch"
            class="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-orange-400"
          />
        </div>
        <!-- Add MOA button -->
        <button
          @click="showModal = true"
          class="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition whitespace-nowrap"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          Add MOA
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="w-full text-sm text-left">
        <thead>
          <tr class="border-b border-gray-200 text-xs uppercase tracking-wider text-gray-400">
            <th class="pb-3 px-2 w-10">#</th>
            <th class="pb-3 px-2">Company</th>
            <th class="pb-3 px-2">Filename</th>
            <th class="pb-3 px-2">Date Added</th>
            <th class="pb-3 px-2">Status</th>
            <th class="pb-3 px-2 text-right">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-if="moas.length === 0">
            <td colspan="6" class="text-center text-gray-400 py-16">
              <div class="flex flex-col items-center gap-2">
                <svg class="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 17v-2a4 4 0 014-4h0a4 4 0 014 4v2M3 21h18M12 3a4 4 0 100 8 4 4 0 000-8z" />
                </svg>
                <span class="text-sm">No MOA found.</span>
              </div>
            </td>
          </tr>

          <tr v-for="(moa, index) in moas" :key="moa.id" class="border-b border-gray-100 hover:bg-gray-50 transition align-top">
            <td class="py-4 px-2 text-gray-400 font-medium">
              {{ (currentPage - 1) * pageSize + index + 1 }}
            </td>

            <td class="py-4 px-2 max-w-xs">
              <span class="font-semibold text-gray-800 break-words whitespace-normal leading-snug">
                {{ toTitleCase(moa.company) }}
              </span>
            </td>

            <td class="py-4 px-2 text-gray-500">
              <span v-if="moa.modified_filename" class="inline-flex items-center gap-1.5">
                <span class="shrink-0 [&>svg]:w-5 [&>svg]:h-5">
                  <ExcelIcon v-if="moa.fileType === 'excel'" />
                  <MSWordIcon v-else-if="moa.fileType === 'word'" />
                  <PDFIcon v-else-if="moa.fileType === 'pdf'" />
                </span>
                {{ moa.orig_filename }}
              </span>
              <span v-else class="text-gray-300">—</span>
            </td>

            <td class="py-4 px-2 text-gray-400 text-xs whitespace-nowrap">
              {{
                new Date(moa.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              }}
            </td>

            <td class="py-4 px-2">
              <span
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                :class="moa.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'"
              >
                {{ moa.status }}
              </span>
            </td>

            <td class="py-4 px-2 text-right flex justify-end gap-2">
              <!-- Download SVG Button -->
              <button @click="viewFile(moa.modified_filename)" class="p-1 text-gray-500 hover:text-gray-700 transition rounded-full" title="View MOA">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-5 h-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.065 7-9.542 7s-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>

              <!-- Delete SVG Button -->
              <button @click="deleteMoa(moa.id)" class="p-1 text-red-500 hover:text-red-700 transition rounded-full" title="Delete MOA">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <HeroPagination :currentPage="currentPage" :totalPages="totalPages" @page-changed="onPageChanged" />

    <!-- Add/Edit Modal -->
    <ProductsOfferedModal v-model="showModal" :isEdit="false" :productData="{}" @save="fetchMoas" />
  </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";
import ProductsOfferedModal from "@/components/sections/products-and-services/Modals/ProductsOfferedModal.vue";
import HeroPagination from "@/components/common/HeroPagination.vue";
import ExcelIcon from "@/assets/svg/ExcelIcon.vue";
import MSWordIcon from "@/assets/svg/MSWordIcon.vue";
import PDFIcon from "@/assets/svg/PDFIcon.vue";
import { toTitleCase } from "@/utils/dateFormat";
import { getAllSignedMoas, deleteSignedMoa, downloadSignedMoa } from "@/api/signedMoa";
import debounce from "lodash/debounce";

const getFileType = (fileName) => {
  if (!fileName) return null;
  const ext = fileName.split(".").pop().toLowerCase();
  if (["xls", "xlsx", "csv"].includes(ext)) return "excel";
  if (["doc", "docx"].includes(ext)) return "word";
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "image";
  return null;
};

export default {
  components: { ProductsOfferedModal, HeroPagination, ExcelIcon, MSWordIcon, PDFIcon },
  setup() {
    const showModal = ref(false);
    const searchQuery = ref("");
    const currentPage = ref(1);
    const pageSize = 7;
    const moas = ref([]);
    const totalPages = ref(1);
    const totalItems = ref(0);

    const fetchMoas = async () => {
      try {
        const res = await getAllSignedMoas({
          page: currentPage.value,
          pageSize,
          search: searchQuery.value || "",
        });

        const data = res.data?.data || [];
        moas.value = data.map((m) => ({
          ...m,
          fileType: getFileType(m.modified_filename),
        }));

        totalPages.value = res.data?.meta?.totalPages || 1;
        totalItems.value = res.data?.meta?.total || data.length;
        currentPage.value = res.data?.meta?.page || 1;
      } catch (err) {
        console.error("Failed to fetch MOAs", err);
      }
    };

    const deleteMoa = async (id) => {
      if (!confirm("Are you sure you want to delete this MOA?")) return;
      try {
        await deleteSignedMoa(id);
        fetchMoas();
      } catch (err) {
        console.error("Failed to delete MOA", err);
      }
    };

    const downloadMoa = async (moa) => {
      if (!moa.modified_filename) return;
      try {
        await downloadSignedMoa(moa.id, moa.orig_filename);
        console.log("Download started for", moa.orig_filename);
      } catch (err) {
        console.error("Failed to download MOA", err);
        alert("Failed to download file. Please try again.");
      }
    };

    const viewFile = (file) => {
      if (!file || typeof file !== "string") return;
      const baseURL = "https://orange-butterfly-425931.hostingersite.com/MOA_Files/";
      const url = `${baseURL}${file}`;

      const extension = file.split(".").pop().toLowerCase();

      if (extension === "pdf") {
        // Open PDF in a new tab using Google Docs Viewer (only works if public)
        window.open(`https://docs.google.com/gview?url=${url}&embedded=true`, "_blank");
      } else if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
        window.open(url, "_blank"); // open images directly
      } else {
        window.open(url, "_blank"); // fallback for other file types
      }
    };

    const debouncedFetch = debounce(() => {
      currentPage.value = 1;
      fetchMoas();
    }, 300);

    const onPageChanged = (page) => {
      currentPage.value = page;
      fetchMoas();
    };

    watch(searchQuery, debouncedFetch);
    onMounted(fetchMoas);

    return {
      showModal,
      searchQuery,
      currentPage,
      pageSize,
      moas,
      totalPages,
      totalItems,
      fetchMoas,
      deleteMoa,
      downloadMoa,
      viewFile,
      debouncedFetch,
      onPageChanged,
      toTitleCase,
    };
  },
};
</script>
